import { YOU } from "@/api/memory-focus";
import { AVATAR_SIZE, type AvatarKind } from "@/lib/memory-thing-kinds";
import { clamp, lerp, smoothstep } from "./globe-projection";
import {
  FOCUS_HINT_FONT,
  FOCUS_LABEL_FONT,
  LABEL_FONT,
  type PlacedLabel,
} from "./globe-labels";
import {
  DIM,
  type DrawOptions,
  type GlobePalette,
  type ProjectedNode,
  type ProjectedScene,
  type Rgba,
} from "./globe-render-types";
import { rgba } from "./globe-palette";

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';

const BANDS = 8;
const FILL: Record<Exclude<AvatarKind, "topic">, number> = {
  you: 0.1,
  person: 0.13,
  project: 0.15,
  app: 0.11,
  document: 0.15,
};
const MASK: Rgba = { r: 0, g: 0, b: 0, a: 1 };
function strokePath(
  ctx: CanvasRenderingContext2D,
  color: Rgba,
  alpha: number,
): void {
  ctx.strokeStyle = rgba(color, alpha);
  ctx.stroke();
}
function strokeSegments(
  ctx: CanvasRenderingContext2D,
  segments: readonly number[],
  color: Rgba,
  alpha: number,
): void {
  if (segments.length === 0) return;
  ctx.beginPath();
  for (let i = 0; i < segments.length; i += 4) {
    ctx.moveTo(segments[i]!, segments[i + 1]!);
    ctx.lineTo(segments[i + 2]!, segments[i + 3]!);
  }
  strokePath(ctx, color, alpha);
}
/** The segments of an arc on one side of the globe (mockup `segs`), each with its mean depth. */
function eachSegment(
  points: readonly number[],
  front: boolean,
  each: (segment: number[], z: number) => void,
): void {
  for (let i = 0; i + 5 < points.length; i += 3) {
    const z = (points[i + 2]! + points[i + 5]!) / 2;
    if (z >= 0 === front)
      each([points[i]!, points[i + 1]!, points[i + 3]!, points[i + 4]!], z);
  }
}
export function strokeArcs(
  ctx: CanvasRenderingContext2D,
  scene: ProjectedScene,
  options: DrawOptions,
  front: boolean,
): void {
  const { ink, amber, accent } = options.palette;
  const [high, low] = scene.dense ? [0.075, 0.024] : [0.14, 0.05];
  const dim = lerp(1, DIM, scene.dimmed);
  const hoverAtRoot = scene.focusKey === null ? options.hoverKey : null;
  const [bands, hot, recent, ghosts]: [
    number[][],
    number[],
    number[],
    number[],
  ] = [Array.from({ length: BANDS }, () => []), [], [], []];
  for (const { arc, points } of scene.arcs) {
    if (points.length === 0 || arc.focus) continue;
    const lit =
      options.hotArcKeys.has(arc.key) ||
      (hoverAtRoot !== null &&
        (arc.from === hoverAtRoot || arc.to === hoverAtRoot));
    eachSegment(points, front, (segment, z) => {
      const alpha = lerp(low, high, smoothstep(-0.15, 0.15, z)) * dim;
      (arc.suggestionId
        ? ghosts
        : lit
          ? hot
          : arc.recent
            ? recent
            : bands[
                clamp(Math.round((alpha / high) * (BANDS - 1)), 0, BANDS - 1)
              ]!
      ).push(...segment);
    });
  }
  ctx.lineWidth = 1;
  bands.forEach((segments, band) => {
    if (band > 0)
      strokeSegments(ctx, segments, ink, (high * band) / (BANDS - 1));
  });
  strokeSegments(ctx, hot, ink, front ? 0.5 : 0.16);
  strokeSegments(ctx, recent, accent, (front ? 0.38 : 0.14) * dim);
  ctx.setLineDash([4, 4]);
  strokeSegments(ctx, ghosts, amber, (front ? 0.7 : 0.25) * dim);
  for (const { arc, points } of scene.arcs) {
    const vis = Math.min(
      scene.byKey.get(arc.from)?.vis ?? 0,
      scene.byKey.get(arc.to)?.vis ?? 0,
    );
    if (!arc.focus || points.length === 0 || vis < 0.02) continue;
    const segments: number[] = [];
    eachSegment(points, front, (segment) => void segments.push(...segment));
    ctx.setLineDash(arc.suggestionId ? [4, 4] : []);
    strokeSegments(
      ctx,
      segments,
      arc.suggestionId ? amber : arc.recent ? accent : ink,
      (arc.suggestionId
        ? front
          ? 0.7
          : 0.25
        : arc.recent
          ? front
            ? 0.55
            : 0.2
          : front
            ? options.hotArcKeys.has(arc.key)
              ? 0.8
              : 0.5
            : 0.16) * vis,
    );
  }
  ctx.setLineDash([]);
}
/** You's facts, drawn from the centre (mockup drawSpokes). */
export function strokeSpokes(
  ctx: CanvasRenderingContext2D,
  scene: ProjectedScene,
  options: DrawOptions,
): void {
  const you = scene.byKey.get(YOU);
  const [high, low] = scene.dense ? [0.1, 0.035] : [0.14, 0.06];
  const dim = lerp(1, DIM, scene.dimmed);
  for (const { arc, points } of scene.arcs) {
    const other =
      you && points.length === 0
        ? scene.byKey.get(arc.from === YOU ? arc.to : arc.from)
        : undefined;
    if (!you || !other || other.key === YOU) continue;
    const [dx, dy, r0, r1] = [
      other.x - you.x,
      other.y - you.y,
      17,
      other.half * other.scale + 4,
    ];
    const length = Math.hypot(dx, dy);
    if (length < r0 + r1 + 2) continue;
    const front = other.z > -0.05;
    const hot =
      options.hotArcKeys.has(arc.key) ||
      (scene.focusKey === null && options.hoverKey === other.key);
    const alpha = arc.suggestionId
      ? (front ? 0.7 : 0.25) * dim
      : arc.recent && !hot
        ? (front ? 0.4 : 0.14) * dim
        : hot
          ? front
            ? 0.8
            : 0.2
          : arc.focus
            ? front
              ? 0.5
              : 0.16
            : lerp(low, high, smoothstep(-0.15, 0.15, other.z)) * dim;
    ctx.setLineDash(arc.suggestionId ? [4, 4] : []);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(you.x + (dx / length) * r0, you.y + (dy / length) * r0);
    ctx.lineTo(other.x - (dx / length) * r1, other.y - (dy / length) * r1);
    strokePath(
      ctx,
      arc.suggestionId
        ? options.palette.amber
        : arc.recent && !hot
          ? options.palette.accent
          : options.palette.ink,
      alpha * other.tempAlpha,
    );
  }
  ctx.setLineDash([]);
}
function shapePath(
  ctx: CanvasRenderingContext2D,
  avatar: AvatarKind,
  x: number,
  y: number,
  size: number,
): void {
  const k = size / 24;
  ctx.beginPath();
  if (avatar === "project") ctx.roundRect(x, y, size, size, size / 3);
  else if (avatar === "app") ctx.roundRect(x, y, size, size, size * 0.273);
  else if (avatar === "document") {
    ctx.moveTo(x + 6.5 * k, y + 3.5 * k);
    ctx.lineTo(x + 14 * k, y + 3.5 * k);
    ctx.lineTo(x + 18 * k, y + 7.5 * k);
    ctx.lineTo(x + 18 * k, y + 20.5 * k);
    ctx.lineTo(x + 6.5 * k, y + 20.5 * k);
    ctx.closePath();
  } else
    ctx.arc(
      x + size / 2,
      y + size / 2,
      avatar === "topic" ? 4 * k : size / 2,
      0,
      Math.PI * 2,
    );
}
function strokeCircle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: Rgba,
  alpha: number,
): void {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  strokePath(ctx, color, alpha);
}
/** One thing (mockup drawGlobeNode and sprite): its avatar clears the lines under it, then fills; back things shrink and fade; a
 * dashed amber ring marks a suggested thing and a quiet ring the focus. */
export function drawNode(
  ctx: CanvasRenderingContext2D,
  node: ProjectedNode,
  focusKey: string | null,
  palette: GlobePalette,
): void {
  const [s, alpha] =
    node.key === YOU ? [1, node.vis] : [node.scale, node.depthAlpha * node.vis];
  if (alpha < 0.01) return;
  const size = AVATAR_SIZE[node.avatar] * s;
  const [x, y, k, ring] = [
    node.x - size / 2,
    node.y - size / 2,
    size / 24,
    (node.half + 4) * s,
  ];
  shapePath(ctx, node.avatar, x, y, size);
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = rgba(MASK, alpha);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle =
    node.avatar === "topic"
      ? rgba(palette.muted, alpha)
      : rgba(palette.ink, FILL[node.avatar] * alpha);
  ctx.fill();
  if (node.avatar === "document") {
    ctx.beginPath();
    ctx.moveTo(x + 14 * k, y + 3.5 * k);
    ctx.lineTo(x + 14 * k, y + 7.5 * k);
    ctx.lineTo(x + 18 * k, y + 7.5 * k);
    ctx.closePath();
    ctx.fillStyle = rgba(palette.ink, 0.24 * alpha);
    ctx.fill();
  } else if (node.avatar !== "topic") {
    ctx.font = `${node.avatar === "app" ? 700 : 600} ${11 * s}px ${FONT_STACK}`;
    ctx.fillStyle = rgba(palette.ink, 0.95 * alpha);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(node.glyph, node.x, node.y + 0.5);
  }
  ctx.lineWidth = 1;
  if (node.key === YOU)
    strokeCircle(
      ctx,
      node.x,
      node.y,
      size / 2 - 0.5,
      palette.ink,
      0.22 * alpha,
    );
  if (node.pending) {
    ctx.setLineDash([3, 3]);
    strokeCircle(ctx, node.x, node.y, ring, palette.amber, 0.7 * alpha);
    ctx.setLineDash([]);
  } else if (node.recent) {
    strokeCircle(ctx, node.x, node.y, ring, palette.accent, 0.42 * alpha);
  }
  if (node.key === focusKey)
    strokeCircle(ctx, node.x, node.y, ring, palette.ink, 0.45 * alpha);
}
/** A placed label (spec §7.1: 11px at .75, the focus 13px): a 3px halo cleared out of the lines, the name, then a quiet "+N". */
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  label: PlacedLabel,
  node: ProjectedNode,
  palette: GlobePalette,
): void {
  const alpha =
    (label.forced ? Math.max(node.key === YOU ? 1 : node.depthAlpha, 0.8) : 1) *
    node.tempAlpha;
  const [nameFont, hintFont, hintX] = [
    label.big ? FOCUS_LABEL_FONT : LABEL_FONT,
    label.big ? FOCUS_HINT_FONT : LABEL_FONT,
    label.x + label.nameWidth + 4,
  ];
  const write = (
    method: "strokeText" | "fillText",
    nameStyle: string,
    hintStyle: string,
  ) => {
    ctx.font = nameFont;
    ctx.strokeStyle = nameStyle;
    ctx.fillStyle = nameStyle;
    ctx[method](label.text, label.x, label.y);
    if (label.hint) {
      ctx.font = hintFont;
      ctx.strokeStyle = hintStyle;
      ctx.fillStyle = hintStyle;
      ctx[method](label.hint, hintX, label.y);
    }
  };
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.lineJoin = "round";
  ctx.lineWidth = 3;
  ctx.globalCompositeOperation = "destination-out";
  write("strokeText", rgba(MASK, 0.9 * alpha), rgba(MASK, 0.9 * alpha));
  ctx.globalCompositeOperation = "source-over";
  write(
    "fillText",
    rgba(palette.ink, (label.big ? 0.95 : 0.75) * alpha),
    rgba(palette.ink, 0.35 * alpha),
  );
}
